import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store/store";
import  { persistor } from "../store/store";
import DashboardLayout from "./DashboardLayout";
import CBBEvaluation from "./CBBEvaluation";
import SecondaryEvaluation from "./SecondaryEvaluation";
import PrimaryEvaluation from "./PrimaryEvaluation";
import ProductEvaluation from "./ProductEvaluation";
import NetWeightEvaluation from "./NetWeightEvaluation";
import { fetchSummaryData } from "../Services/getSummaryData";
import { getAccessToken } from "../Services/getAccessToken";
import {  setSummaryData, setCycleData } from "../store/planTourSlice";
import { fetchCycleDetails } from "../Services/getCycleDetails";
// @ts-ignore
import moment from "moment";

type CycleResult = {
  started: boolean;
  completed: boolean;
  defects: string[]; // holds item names like "CBB 1"
  okays: string[];
  defectCategories: { [item: string]: string }; // stores defect categories for each item
  evaluationTypes: { [item: string]: string }; // stores evaluation types for each item
  defectRemarks: { [item: string]: string }; // stores defect remarks for each item
  okayEvaluationTypes: { [item: string]: string }; // stores evaluation types for okay items
  missedEvaluationTypes: { [item: string]: string }; // stores evaluation types for missed items
};

type CycleStatusMap = {
  [cycleNo: number]: CycleResult;
};





const totalCycles = 8;

const ProductQualityIndex: React.FC = () => {
  const [activeCycle, setActiveCycle] = useState<number>(1);
  const [cycleStatus, setCycleStatus] = useState<CycleStatusMap>(
    Object.fromEntries(
      Array.from({ length: totalCycles }, (_, i) => [
        i + 1,
        { started: false, completed: false, defects: [], okays: [], defectCategories: {}, evaluationTypes: {}, defectRemarks: {}, okayEvaluationTypes: {}, missedEvaluationTypes: {} },
      ])
    )
  );
  const [showDetails, setShowDetails] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isProcessingCycleCompletion, setIsProcessingCycleCompletion] = useState(false);
  const [selectedEvaluationType, setSelectedEvaluationType] = useState<'CBB' | 'Secondary' | 'Primary' | 'Product' | 'NetWeight'>('CBB');
  const [brokenPercentage, setBrokenPercentage] = useState<string>('0.00');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedShift = useSelector((state: RootState) => state.planTour.selectedCycle);
  const plantTourId = useSelector((state: RootState) => state.planTour.plantTourId);
  const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);
  const offlineSubmissions = useSelector((state: RootState) => state.appState.offlineSubmissions);
  const offlineSubmissionsByCategory = useSelector((state: RootState) => state.appState.offlineSubmissionsByCategory);
  const reduxSummaryData = useSelector((state: RootState) => state.planTour.summaryData);
  const reduxCycleData = useSelector((state: RootState) => state.planTour.cycleData);
  const lastFetchTimestamp = useSelector((state: RootState) => state.planTour.lastFetchTimestamp);



  // Update the fetchAndDisplaySummary function to use both APIs
  // Check if we have cached data and if it's still valid (less than 5 minutes old)
  const isCacheValid = () => {
    if (!lastFetchTimestamp) return false;
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    return (Date.now() - lastFetchTimestamp) < fiveMinutes;
  };

  // Flatten offlineSubmissionsByCategory into records (same format as API/cycle data)
  const getFlattenedOfflineRecords = (): any[] => {
    if (!offlineSubmissionsByCategory || typeof offlineSubmissionsByCategory !== 'object') return [];
    return Object.values(offlineSubmissionsByCategory).flatMap((subs: any[]) =>
      (subs || []).flatMap((s: any) => s.records || [])
    );
  };

  // Function to fetch completed cycle details using reduxCycleData
  const fetchCompletedCycleDetails = () => {
    let cycleDataToProcess = reduxCycleData;
    
    // In offline mode, use redux cycle data or flattened offline submissions by category
    if (isOfflineStarted) {
      if (reduxCycleData && reduxCycleData.length > 0) {
        console.log("Offline mode: Using reduxCycleData for completed cycle details:", reduxCycleData.length, "records");
        cycleDataToProcess = reduxCycleData;
      } else {
        const flattenedOffline = getFlattenedOfflineRecords();
        if (flattenedOffline.length > 0) {
          console.log("Offline mode: Using offlineSubmissionsByCategory for completed cycle details:", flattenedOffline.length, "records");
          cycleDataToProcess = flattenedOffline;
      } else if (offlineSubmissions && offlineSubmissions.length > 0) {
        console.log("Offline mode: Using offline submissions for completed cycle details");
        cycleDataToProcess = offlineSubmissions.flatMap(submission => submission.records);
      } else {
        console.log("Offline mode: No cycle data available");
        return;
        }
      }
    } else {
      if (!reduxCycleData || reduxCycleData.length === 0) {
        console.log("No cycle data available in Redux");
        return;
      }
      console.log("Using reduxCycleData to fetch completed cycle details:", reduxCycleData.length, "records");
    }
    
    // Process the cycle data to determine completed cycles
    const completedCycles = new Set<number>();
    const cycleDetails: { [cycleNo: number]: { defects: string[], okays: string[], defectCategories: { [item: string]: string }, evaluationTypes: { [item: string]: string }, defectRemarks: { [item: string]: string }, okayEvaluationTypes: { [item: string]: string }, missedEvaluationTypes: { [item: string]: string } } } = {};
    
    cycleDataToProcess.forEach((item: any) => {
      const cycleMatch = item.cr3ea_cycle?.match(/Cycle-(\d+)/);
      if (cycleMatch) {
        const cycleNo = parseInt(cycleMatch[1]);
        completedCycles.add(cycleNo);
        
        if (!cycleDetails[cycleNo]) {
          cycleDetails[cycleNo] = { 
            defects: [], 
            okays: [], 
            defectCategories: {}, 
            evaluationTypes: {}, 
            defectRemarks: {}, 
            okayEvaluationTypes: {}, 
            missedEvaluationTypes: {} 
          };
        }
        
        // Process based on criteria
        const itemName = item.cr3ea_evaluationtype || item.cr3ea_defect || 'Unknown';
        if (item.cr3ea_criteria === 'Okay') {
          cycleDetails[cycleNo].okays.push(itemName);
          cycleDetails[cycleNo].okayEvaluationTypes[itemName] = itemName;
        } else if (item.cr3ea_criteria === 'Not Okay') {
          if (item.cr3ea_defectcategory === 'Missed') {
            cycleDetails[cycleNo].missedEvaluationTypes[itemName] = itemName;
          } else {
            cycleDetails[cycleNo].defects.push(itemName);
            cycleDetails[cycleNo].defectCategories[itemName] = item.cr3ea_defectcategory || 'Category B';
            cycleDetails[cycleNo].evaluationTypes[itemName] = itemName;
            cycleDetails[cycleNo].defectRemarks[itemName] = item.cr3ea_defectremarks || '';
          }
        } else if (!item.cr3ea_criteria || item.cr3ea_criteria === null) {
          cycleDetails[cycleNo].missedEvaluationTypes[itemName] = itemName;
        }
      }
    });
    
    console.log('Processed cycle details from reduxCycleData:', cycleDetails);
    
    // Check if cycle status actually needs to be updated
    const newCycleStatus = { ...cycleStatus };
    let hasChanges = false;
    
    completedCycles.forEach(cycleNo => {
      const newStatus = {
        started: true,
        completed: true,
        defects: cycleDetails[cycleNo]?.defects || [],
        okays: cycleDetails[cycleNo]?.okays || [],
        defectCategories: cycleDetails[cycleNo]?.defectCategories || {},
        evaluationTypes: cycleDetails[cycleNo]?.evaluationTypes || {},
        defectRemarks: cycleDetails[cycleNo]?.defectRemarks || {},
        okayEvaluationTypes: cycleDetails[cycleNo]?.okayEvaluationTypes || {},
        missedEvaluationTypes: cycleDetails[cycleNo]?.missedEvaluationTypes || {}
      };
      
      // Compare with existing status to avoid unnecessary updates
      const existingStatus = cycleStatus[cycleNo];
      if (!existingStatus || 
          JSON.stringify(existingStatus) !== JSON.stringify(newStatus)) {
        newCycleStatus[cycleNo] = newStatus;
        hasChanges = true;
      }
    });
    
    // Find the next available cycle (first non-completed cycle)
    let nextAvailableCycle = 1;
    while (nextAvailableCycle <= totalCycles && completedCycles.has(nextAvailableCycle)) {
      nextAvailableCycle++;
    }
    
    // Only update state if there are actual changes
    if (hasChanges || nextAvailableCycle !== activeCycle) {
      setCycleStatus(newCycleStatus);
      setActiveCycle(nextAvailableCycle);
      console.log('Updated cycle status from reduxCycleData:', newCycleStatus);
      console.log('Next available cycle:', nextAvailableCycle);
    } else {
      console.log('No changes detected in cycle status, skipping update');
    }
  };

  const fetchAndDisplaySummary = async (accessToken: string, qualityTourId: string) => {
    setIsLoadingSummary(true);
    
    // Check if we have valid cached data
    if (reduxSummaryData.length > 0 && reduxCycleData.length > 0 && isCacheValid()) {
      console.log("Using cached data from Redux");
      processApiData(reduxSummaryData, reduxCycleData);
      // Also fetch completed cycle details from reduxCycleData
      fetchCompletedCycleDetails();
      setIsLoadingSummary(false);
      return;
    }
    
    try {
      console.log("Fetching fresh data from API");
      // Use both APIs: fetchSummaryData for summary and fetchCycleDetails for cycle details
      const [summaryData, cycleData]: [any[] | null, any[]] = await Promise.all([
        fetchSummaryData(accessToken, qualityTourId),
        fetchCycleDetails(accessToken, qualityTourId)
      ]);
      
      console.log("Fetched Summary Data:", summaryData);
      console.log("Fetched Cycle Records:", cycleData);
       
      // Store data in Redux
      dispatch(setSummaryData(summaryData || []));
      dispatch(setCycleData(cycleData || []));
      
      // Process the data
      processApiData(summaryData || [], cycleData || []);
      
    } catch (error) {
      console.error("Error loading data:", error);
      dispatch(setSummaryData([]));
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // Separate function to process API data
  const processApiData = (summaryData: any[], cycleData: any[]) => {
    // Process summary data
    if (!summaryData || summaryData.length === 0) {
      dispatch(setSummaryData([]));
    } else {
      dispatch(setSummaryData(summaryData));
    }
    
    // Process cycle details to determine completed cycles
    const completedCycles = new Set<number>();
    const cycleDetails: { [cycleNo: number]: { defects: string[], okays: string[], defectCategories: { [item: string]: string }, evaluationTypes: { [item: string]: string }, defectRemarks: { [item: string]: string }, okayEvaluationTypes: { [item: string]: string }, missedEvaluationTypes: { [item: string]: string } } } = {};
    
    if (cycleData && cycleData.length > 0) {
      console.log('Processing cycle data:', cycleData.length, 'records');
      
      cycleData.forEach((item: any) => {
        const cycleMatch = item.cr3ea_cycle?.match(/Cycle-(\d+)/);
        if (cycleMatch) {
          const cycleNo = parseInt(cycleMatch[1]);
          completedCycles.add(cycleNo);
          
          if (!cycleDetails[cycleNo]) {
            cycleDetails[cycleNo] = { 
              defects: [], 
              okays: [], 
              defectCategories: {}, 
              evaluationTypes: {}, 
              defectRemarks: {}, 
              okayEvaluationTypes: {}, 
              missedEvaluationTypes: {} 
            };
          }
          
          // Process based on criteria
          if (item.cr3ea_criteria === 'Okay') {
            const evaluationType = item.cr3ea_evaluationtype || item.cr3ea_defect || 'Unknown';
            cycleDetails[cycleNo].okays.push(evaluationType);
            cycleDetails[cycleNo].okayEvaluationTypes[evaluationType] = evaluationType;
          } else if (item.cr3ea_criteria === 'Not Okay') {
            const defectItem = item.cr3ea_defect || item.cr3ea_evaluationtype || 'Unknown';
            cycleDetails[cycleNo].defects.push(defectItem);
            cycleDetails[cycleNo].defectCategories[defectItem] = item.cr3ea_defectcategory || 'Category B';
            cycleDetails[cycleNo].evaluationTypes[defectItem] = item.cr3ea_evaluationtype || defectItem;
            cycleDetails[cycleNo].defectRemarks[defectItem] = item.cr3ea_defectremarks || '';
          } else if (!item.cr3ea_criteria || item.cr3ea_criteria === null) {
            // Handle missed evaluations
            const missedItem = item.cr3ea_evaluationtype || item.cr3ea_defect || 'Unknown';
            cycleDetails[cycleNo].missedEvaluationTypes[missedItem] = missedItem;
          }
        }
      });
      
      console.log('Processed cycle details:', cycleDetails);
    }
    
    // Update cycle status based on cycle details API data
    const newCycleStatus = { ...cycleStatus };
    completedCycles.forEach(cycleNo => {
      newCycleStatus[cycleNo] = {
        started: true,
        completed: true,
        defects: cycleDetails[cycleNo]?.defects || [],
        okays: cycleDetails[cycleNo]?.okays || [],
        defectCategories: cycleDetails[cycleNo]?.defectCategories || {},
        evaluationTypes: cycleDetails[cycleNo]?.evaluationTypes || {},
        defectRemarks: cycleDetails[cycleNo]?.defectRemarks || {},
        okayEvaluationTypes: cycleDetails[cycleNo]?.okayEvaluationTypes || {},
        missedEvaluationTypes: cycleDetails[cycleNo]?.missedEvaluationTypes || {}
      };
    });
    
    // Find the next available cycle (first non-completed cycle)
    let nextAvailableCycle = 1;
    while (nextAvailableCycle <= totalCycles && completedCycles.has(nextAvailableCycle)) {
      nextAvailableCycle++;
    }
    
    setActiveCycle(nextAvailableCycle);
    setCycleStatus(newCycleStatus);
    
    console.log('Updated cycle status:', newCycleStatus);
    console.log('Next available cycle:', nextAvailableCycle);
  };


  // Process offline data for summary display
  const processOfflineData = () => {
    if (!isOfflineStarted) {
      return;
    }

    // If we have Redux data, use that instead of offline submissions
    if (reduxCycleData && reduxCycleData.length > 0) {
      console.log("Offline mode: Using Redux cycle data instead of offline submissions");
      return; // Let fetchCompletedCycleDetails handle this
    }

    const flattenedOffline = getFlattenedOfflineRecords();
    if (flattenedOffline.length === 0 && (!offlineSubmissions || offlineSubmissions.length === 0)) {
      return;
    }

    const recordsToProcess = flattenedOffline.length > 0 ? flattenedOffline : offlineSubmissions.flatMap(submission => submission.records);
    console.log("Offline mode: Processing offline submissions for cycle details:", recordsToProcess.length, "records");
    
    // Group records by cycle for cycle status (PQI uses flat cycle status)
    const cycleRecordsMap: { [cycleNo: number]: any[] } = {};
    recordsToProcess.forEach((record: any) => {
      const cycleMatch = record.cr3ea_cycle?.match(/Cycle-(\d+)/);
      if (cycleMatch) {
        const cycleNo = parseInt(cycleMatch[1]);
        if (!cycleRecordsMap[cycleNo]) cycleRecordsMap[cycleNo] = [];
        cycleRecordsMap[cycleNo].push(record);
      }
    });
    
    // Update cycle status based on offline records
    const newCycleStatus = { ...cycleStatus };
    Object.entries(cycleRecordsMap).forEach(([cycleNoStr, records]) => {
      const cycleNo = parseInt(cycleNoStr);
      
      const defects: string[] = [];
      const okays: string[] = [];
      const defectCategories: { [key: string]: string } = {};
      const evaluationTypes: { [key: string]: string } = {};
      const defectRemarks: { [key: string]: string } = {};
      const okayEvaluationTypes: { [key: string]: string } = {};
      const missedEvaluationTypes: { [key: string]: string } = {};
      
      records.forEach((record: any) => {
        const itemName = record.cr3ea_evaluationtype || record.cr3ea_defect || 'Unknown';
        if (record.cr3ea_criteria === 'Okay') {
          okays.push(itemName);
          okayEvaluationTypes[itemName] = itemName;
        } else if (record.cr3ea_criteria === 'Not Okay') {
          if (record.cr3ea_defectcategory === 'Missed') {
            missedEvaluationTypes[itemName] = itemName;
          } else {
            defects.push(itemName);
            defectCategories[itemName] = record.cr3ea_defectcategory || 'Category B';
            evaluationTypes[itemName] = itemName;
            defectRemarks[itemName] = record.cr3ea_defectremarks || '';
          }
        }
      });
      
      newCycleStatus[cycleNo] = {
        started: true,
        completed: true,
        defects,
        okays,
        defectCategories,
        evaluationTypes,
        defectRemarks,
        okayEvaluationTypes,
        missedEvaluationTypes
      };
    });
    
    // Find the next available cycle (first non-completed cycle)
    let nextAvailableCycle = 1;
    while (nextAvailableCycle <= totalCycles && newCycleStatus[nextAvailableCycle]?.completed) {
      nextAvailableCycle++;
    }
    
    setCycleStatus(newCycleStatus);
    setActiveCycle(nextAvailableCycle);
    console.log('Offline data processed for summary display');
    console.log('Processed cycle status:', newCycleStatus);
    console.log('Next available cycle:', nextAvailableCycle);
  };

  // Use this function after save and in useEffect
  useEffect(() => {
    const fetchSummary = async () => {
      if (!plantTourId) {
        dispatch(setSummaryData([]));
        return;
      }
      
      // If in offline mode, use Redux data or offlineSubmissionsByCategory for summary and cycle details
      if (isOfflineStarted) {
        console.log("Offline mode: Using Redux or offline data for summary and cycle details");
        if (reduxSummaryData && reduxSummaryData.length > 0) {
          console.log("Using cached summary data from Redux for offline mode");
        }
        // Fetch completed cycle details from reduxCycleData or offlineSubmissionsByCategory
        const hasOfflineData = getFlattenedOfflineRecords().length > 0;
        if (reduxCycleData && reduxCycleData.length > 0) {
          console.log("Using cached cycle data from Redux for offline mode");
        } else if (hasOfflineData) {
          console.log("Using offlineSubmissionsByCategory for cycle details");
        }
        fetchCompletedCycleDetails();
        processOfflineData();
        return;
      }
      
      // Otherwise fetch from API
      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      if (!accessToken) return;
      await fetchAndDisplaySummary(accessToken, plantTourId);
    };
    fetchSummary();
  }, [plantTourId, isOfflineStarted, offlineSubmissions, offlineSubmissionsByCategory]);

  // Effect to fetch completed cycle details when reduxCycleData changes
  useEffect(() => {
    if (reduxCycleData && reduxCycleData.length > 0 && !isOfflineStarted && !isProcessingCycleCompletion) {
      // Add a check to prevent processing if data hasn't actually changed
      const currentDataHash = JSON.stringify(reduxCycleData);
      const lastProcessedHash = localStorage.getItem('lastProcessedPQICycleDataHash');
      
      if (currentDataHash !== lastProcessedHash) {
        console.log("reduxCycleData changed, fetching completed cycle details");
        fetchCompletedCycleDetails();
        localStorage.setItem('lastProcessedPQICycleDataHash', currentDataHash);
      } else {
        console.log("reduxCycleData unchanged, skipping cycle details processing");
      }
    }
  }, [reduxCycleData, isOfflineStarted, isProcessingCycleCompletion]);

  // Debug effect to log Redux data persistence (only on mount and when plantTourId changes)
  useEffect(() => {
    console.log("Redux Data Status:", {
      summaryDataLength: reduxSummaryData?.length || 0,
      cycleDataLength: reduxCycleData?.length || 0,
      lastFetchTimestamp,
      isOfflineStarted,
      plantTourId
    });
  }, [plantTourId]); // Only log when plantTourId changes, not on every data change

  // Cleanup effect to clear localStorage when plantTourId changes or component unmounts
  useEffect(() => {
    return () => {
      // Clear the data hash when component unmounts
      localStorage.removeItem('lastProcessedPQICycleDataHash');
    };
  }, [plantTourId]);

  // Clear localStorage when plantTourId changes to ensure fresh data processing
  useEffect(() => {
    localStorage.removeItem('lastProcessedPQICycleDataHash');
    console.log('ProductQualityIndex: Cleared localStorage data hash due to plantTourId change');
  }, [plantTourId]);

  const onCycleComplete = async () => {
    // Set flag to prevent infinite loops
    setIsProcessingCycleCompletion(true);
    
    try {
      // Refresh summary after cycle completion
      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      if (accessToken) {
        await fetchAndDisplaySummary(accessToken, plantTourId || "");
      }
    } finally {
      // Clear flag after processing
      setIsProcessingCycleCompletion(false);
    }
  };

  // Calculate summary statistics from API data or offline data
  const calculateSummaryStats = () => {
    // Use reduxCycleData if available (from fetchCycleDetails) - it has all the data
    // Otherwise fall back to reduxSummaryData (from fetchSummaryData)
    let dataToProcess = reduxCycleData && reduxCycleData.length > 0 ? reduxCycleData : reduxSummaryData;
    
    console.log('calculateSummaryStats: Using data source:', {
      usingCycleData: reduxCycleData && reduxCycleData.length > 0,
      cycleDataLength: reduxCycleData?.length || 0,
      summaryDataLength: reduxSummaryData?.length || 0
    });
    
    // In offline mode, prioritize Redux data, then offlineSubmissionsByCategory, then offlineSubmissions
    if (isOfflineStarted) {
      if (reduxCycleData && reduxCycleData.length > 0) {
        console.log('Offline mode: Using reduxCycleData for summary statistics');
        dataToProcess = reduxCycleData;
      } else if (reduxSummaryData && reduxSummaryData.length > 0) {
        console.log('Offline mode: Using reduxSummaryData for summary statistics');
        dataToProcess = reduxSummaryData;
      } else {
        const flattenedOffline = getFlattenedOfflineRecords();
        if (flattenedOffline.length > 0) {
          console.log('Offline mode: Using offlineSubmissionsByCategory for summary statistics:', flattenedOffline.length, 'records');
          dataToProcess = flattenedOffline;
      } else if (offlineSubmissions && offlineSubmissions.length > 0) {
        console.log('Offline mode: Using offline submissions for summary statistics');
        dataToProcess = offlineSubmissions.flatMap(submission => submission.records);
      } else {
        console.log('Offline mode: No data available for summary statistics');
        dataToProcess = [];
        }
      }
    }
    
    if (!dataToProcess || dataToProcess.length === 0) {
      console.warn('calculateSummaryStats: No data to process!');
      return {
        totalDefects: 0,
        totalOkays: 0,
        categories: [],
        finalPQIScore: 0,
        pqiStatus: 'HOLD'
      };
    }
    
    // Log sample of data to see what we're working with
    console.log('calculateSummaryStats: Sample data items:', dataToProcess.slice(0, 5).map((item: any) => ({
      category: item.cr3ea_category,
      cycle: item.cr3ea_cycle,
      criteria: item.cr3ea_criteria,
      defectcategory: item.cr3ea_defectcategory,
      packno: item.cr3ea_packno,
      evaluationtype: item.cr3ea_evaluationtype,
      netweight: item.cr3ea_netweight,
      allFields: Object.keys(item) // Show all available fields
    })));
    
    // Log all items with defects to see what we have
    const itemsWithDefects = dataToProcess.filter((item: any) => {
      const hasDefectCat = item.cr3ea_defectcategory !== null && 
                          item.cr3ea_defectcategory !== undefined && 
                          item.cr3ea_defectcategory !== '';
      const isNotOkay = item.cr3ea_criteria === 'Not Okay';
      return hasDefectCat || isNotOkay;
    });
    
    console.log('=== DATA ANALYSIS ===');
    console.log('calculateSummaryStats: ALL items in dataToProcess:', dataToProcess.length);
    console.log('calculateSummaryStats: Items with defects (by filter):', itemsWithDefects.length);
    
    // Count defects by category manually to verify
    const manualCount: Record<string, { a: number; b: number; c: number }> = {};
    dataToProcess.forEach((item: any) => {
      const cat = item.cr3ea_category || 'Unknown';
      if (!manualCount[cat]) {
        manualCount[cat] = { a: 0, b: 0, c: 0 };
      }
      
      const defectCat = item.cr3ea_defectcategory;
      if (defectCat !== null && defectCat !== undefined && defectCat !== '') {
        const defectStr = String(defectCat).toUpperCase();
        if (defectStr.includes('A') && !defectStr.includes('B') && !defectStr.includes('C')) {
          manualCount[cat].a++;
        } else if (defectStr.includes('B') && !defectStr.includes('C')) {
          manualCount[cat].b++;
        } else if (defectStr.includes('C')) {
          manualCount[cat].c++;
        }
      }
    });
    
    console.log('Manual defect count by category:', manualCount);
    
    // Log ALL raw data to see what we're actually getting
    console.log('calculateSummaryStats: RAW DATA DUMP (ALL items with defectcategory):', 
      dataToProcess
        .filter((item: any) => item.cr3ea_defectcategory !== null && item.cr3ea_defectcategory !== undefined)
        .map((item: any) => ({
          category: item.cr3ea_category,
          cycle: item.cr3ea_cycle,
          criteria: item.cr3ea_criteria,
          defectcategory: item.cr3ea_defectcategory,
          defectcategoryType: typeof item.cr3ea_defectcategory,
          packno: item.cr3ea_packno,
          evaluationtype: item.cr3ea_evaluationtype,
          netweight: item.cr3ea_netweight
        }))
    );
    
    itemsWithDefects.forEach((item: any, idx: number) => {
      console.log(`Defect item ${idx + 1}:`, {
        category: item.cr3ea_category,
        cycle: item.cr3ea_cycle,
        criteria: item.cr3ea_criteria,
        defectcategory: item.cr3ea_defectcategory,
        defectcategoryType: typeof item.cr3ea_defectcategory,
        defectcategoryRaw: JSON.stringify(item.cr3ea_defectcategory),
        packno: item.cr3ea_packno,
        evaluationtype: item.cr3ea_evaluationtype
      });
    });
    console.log('=== END DATA ANALYSIS ===');

    let totalDefects = 0;
    let totalOkays = 0;
    const categories: any[] = [];
    
    // Individual bonus scores
    let bonusScores = {
      cbb: 0,
      secondary: 0,
      primary: 0,
      product: 0,
      netWeight: 0
    };

    // Process data by category
    const summary: Record<string, { okays: number; aDefects: number; bDefects: number; cDefects: number }> = {};
    const uniqueCycles = new Set();
    // Track unique cycles per category
    const cyclesPerCategory: Record<string, Set<string>> = {};

    console.log('calculateSummaryStats: Processing dataToProcess:', dataToProcess.length, 'items');
    
    // Log ALL raw items before processing
    console.log('=== RAW DATA FROM API ===');
    console.log('Total items fetched:', dataToProcess.length);
    const netWeightItems = dataToProcess.filter((item: any) => 
      (item.cr3ea_category === 'Net Weight' || item.cr3ea_category === 'Net Weight Evaluation')
    );
    console.log('Net Weight items:', netWeightItems.length);
    netWeightItems.forEach((item: any, idx: number) => {
      console.log(`Net Weight Item ${idx + 1}:`, {
        cycle: item.cr3ea_cycle,
        packno: item.cr3ea_packno,
        criteria: item.cr3ea_criteria,
        defectcategory: item.cr3ea_defectcategory,
        defectcategoryType: typeof item.cr3ea_defectcategory,
        netweight: item.cr3ea_netweight
      });
    });
    console.log('=== END RAW DATA ===');
    
    // Deduplicate records based on unique identifier (cycle + category + evaluationtype/packno + product)
    // This prevents counting the same item multiple times
    // For Net Weight: use cycle + packno
    // For other categories: use cycle + evaluationtype + product+batch (to count multiple products per cycle)
    const seenItems = new Set<string>();
    const uniqueItems = dataToProcess.filter((item: any) => {
      const category = item.cr3ea_category || '';
      let key = '';
      
      if (category === 'Net Weight' || category === 'Net Weight Evaluation') {
        // For Net Weight, use cycle + packno OR evaluationtype (which might contain "Pack 1", "Pack 2", etc.)
        const packNo = item.cr3ea_packno || '';
        const evalType = item.cr3ea_evaluationtype || '';
        // Extract pack number from evaluationtype if packno is not available (e.g., "Pack 1" -> "1")
        let packIdentifier = packNo;
        if (!packIdentifier && evalType) {
          const packMatch = evalType.match(/Pack\s*(\d+)/i);
          if (packMatch) {
            packIdentifier = `Pack ${packMatch[1]}`;
          } else {
            packIdentifier = evalType;
          }
        }
        key = `${item.cr3ea_cycle || ''}_${category}_${packIdentifier}`;
      } else {
        // For CBB, Secondary, Primary, Product: include product+batch and defect category so multiple products
        // per cycle with different defect categories (e.g. one B, one C) are each counted, not collapsed
        const productId = `${item.cr3ea_productname || ''}_${item.cr3ea_batchno || ''}`;
        const defectCat = item.cr3ea_defectcategory != null && item.cr3ea_defectcategory !== '' ? String(item.cr3ea_defectcategory) : '';
        const criteria = item.cr3ea_criteria ?? '';
        key = `${item.cr3ea_cycle || ''}_${category}_${item.cr3ea_evaluationtype || item.cr3ea_defect || ''}_${productId}_${defectCat}_${criteria}`;
      }
      
      if (seenItems.has(key)) {
        console.log('⚠️ Skipping duplicate item:', key);
        return false;
      }
      seenItems.add(key);
      return true;
    });
    
    console.log('calculateSummaryStats: After deduplication:', uniqueItems.length, 'unique items (from', dataToProcess.length, 'total)');
    
    // Log Net Weight items specifically after deduplication
    const netWeightItemsAfterDedup = uniqueItems.filter((item: any) => 
      item.cr3ea_category === 'Net Weight' || item.cr3ea_category === 'Net Weight Evaluation'
    );
    console.log('Net Weight items after deduplication:', netWeightItemsAfterDedup.length);
    netWeightItemsAfterDedup.forEach((item: any, idx: number) => {
      console.log(`Net Weight Item ${idx + 1}:`, {
        cycle: item.cr3ea_cycle,
        packno: item.cr3ea_packno,
        evaluationtype: item.cr3ea_evaluationtype,
        criteria: item.cr3ea_criteria,
        defectcategory: item.cr3ea_defectcategory,
        netweight: item.cr3ea_netweight
      });
    });
    
    // Helper function to normalize defect category (handle option sets, strings, numbers, etc.)
    const normalizeDefectCategory = (defectCat: any): string => {
      if (defectCat === null || defectCat === undefined) {
        return '';
      }
      
      // Handle option set lookups - check if it's a lookup object
      if (typeof defectCat === 'object' && defectCat !== null) {
        // Check various possible properties
        const result = String(defectCat.value || defectCat.label || defectCat.text || defectCat.name || '').trim();
        if (result) return result;
        // If object but no value, try JSON stringify
        try {
          const jsonStr = JSON.stringify(defectCat);
          if (jsonStr && jsonStr !== '{}') return jsonStr;
        } catch (e) {}
      }
      
      // Handle numbers (option set values) - map common Dynamics 365 option set values
      if (typeof defectCat === 'number') {
        // Common Dynamics 365 option set mappings
        // Option sets often use: 1, 2, 3 or 100000000, 100000001, 100000002
        if (defectCat === 1 || defectCat === 100000000 || defectCat === 1000000000) {
          return 'Category A';
        } else if (defectCat === 2 || defectCat === 100000001 || defectCat === 1000000001) {
          return 'Category B';
        } else if (defectCat === 3 || defectCat === 100000002 || defectCat === 1000000002) {
          return 'Category C';
        }
        // Return as string for further processing
        return String(defectCat);
      }
      
      const result = String(defectCat).trim();
      return result;
    };
    
    // Helper function to determine defect category (A, B, or C)
    const getDefectCategoryType = (defectCat: string): 'A' | 'B' | 'C' | null => {
      if (!defectCat) return null;
      
      // Convert to string and uppercase for comparison
      const upper = String(defectCat).toUpperCase().trim();
      
      // Remove any whitespace and normalize
      const cleaned = upper.replace(/\s+/g, ' ').trim();
      
      // Exact matches first (most specific) - check numbers and common formats
      if (cleaned === 'CATEGORY A' || cleaned === 'A' || cleaned === '100000000' || cleaned === '1000000000' || cleaned === '1') {
        return 'A';
      }
      if (cleaned === 'CATEGORY B' || cleaned === 'B' || cleaned === '100000001' || cleaned === '1000000001' || cleaned === '2') {
        return 'B';
      }
      if (cleaned === 'CATEGORY C' || cleaned === 'C' || cleaned === '100000002' || cleaned === '1000000002' || cleaned === '3') {
        return 'C';
      }
      
      // Check for "Category X" pattern - IMPORTANT: Check C first, then B, then A to avoid false matches
      // This prevents "CATEGORY" from matching "A" in "CATEGORY B" or "CATEGORY C"
      if (cleaned.includes('CATEGORY C') || cleaned.includes('CAT C') || cleaned.includes('CATE C') || cleaned.includes('CATEGORYC')) {
        return 'C';
      }
      if (cleaned.includes('CATEGORY B') || cleaned.includes('CAT B') || cleaned.includes('CATE B') || cleaned.includes('CATEGORYB')) {
        return 'B';
      }
      if (cleaned.includes('CATEGORY A') || cleaned.includes('CAT A') || cleaned.includes('CATE A') || cleaned.includes('CATEGORYA')) {
        return 'A';
      }
      
      // Check if it ends with " A", " B", or " C" (space before letter)
      if (cleaned.endsWith(' A') || cleaned.match(/\s+A$/)) return 'A';
      if (cleaned.endsWith(' B') || cleaned.match(/\s+B$/)) return 'B';
      if (cleaned.endsWith(' C') || cleaned.match(/\s+C$/)) return 'C';
      
      // Check if it ends with just the letter (but not as part of another word)
      if (cleaned.endsWith('A') && !cleaned.endsWith('CA') && !cleaned.endsWith('BA') && cleaned !== 'A') return 'A';
      if (cleaned.endsWith('B') && !cleaned.endsWith('CB') && !cleaned.endsWith('AB') && cleaned !== 'B') return 'B';
      if (cleaned.endsWith('C') && !cleaned.endsWith('AC') && !cleaned.endsWith('BC') && cleaned !== 'C') return 'C';
      
      // Check for standalone letter as a word (split by spaces, commas, or other delimiters)
      const words = cleaned.split(/[\s,\-_:;]+/).filter(w => w.length > 0);
      
      // Check for A, B, C as standalone words (not part of CATEGORY)
      const hasA = words.includes('A') && !words.some(w => w.includes('CATEGORY') || w.includes('CAT') || w.includes('CATE'));
      const hasB = words.includes('B') && !words.some(w => w.includes('CATEGORY') || w.includes('CAT') || w.includes('CATE'));
      const hasC = words.includes('C') && !words.some(w => w.includes('CATEGORY') || w.includes('CAT') || w.includes('CATE'));
      
      // Priority: C > B > A (to avoid false matches)
      if (hasC) return 'C';
      if (hasB) return 'B';
      if (hasA) return 'A';
      
      // Check if it's just a single character
      if (cleaned.length === 1) {
        if (cleaned === 'A') return 'A';
        if (cleaned === 'B') return 'B';
        if (cleaned === 'C') return 'C';
      }
      
      // Check if it contains the number patterns (as strings)
      if (cleaned.includes('100000002') || cleaned.includes('1000000002') || cleaned.includes('3')) {
        // Check if it's clearly C and not part of another number
        if (!cleaned.includes('100000001') && !cleaned.includes('1000000001') && !cleaned.includes('2') &&
            !cleaned.includes('100000000') && !cleaned.includes('1000000000') && !cleaned.includes('1')) {
          return 'C';
        }
      }
      if (cleaned.includes('100000001') || cleaned.includes('1000000001') || cleaned.includes('2')) {
        // Check if it's clearly B and not part of another number
        if (!cleaned.includes('100000000') && !cleaned.includes('1000000000') && !cleaned.includes('1')) {
          return 'B';
        }
      }
      if (cleaned.includes('100000000') || cleaned.includes('1000000000') || cleaned.includes('1')) {
        // Check if it's clearly A and not part of B or C numbers
        if (!cleaned.includes('100000001') && !cleaned.includes('1000000001') && !cleaned.includes('2') &&
            !cleaned.includes('100000002') && !cleaned.includes('1000000002') && !cleaned.includes('3')) {
          return 'A';
        }
      }
      
      return null;
    };
    
    uniqueItems.forEach((item: any) => {
      const category = item.cr3ea_category || 'Unknown';
      const cycle = item.cr3ea_cycle;

      if (cycle) {
        uniqueCycles.add(cycle);
      }

      // Normalize category name - ensure similar labels are treated the same
      let normalizedCategory = category;
      if (category === "Net Weight" || category === "Net Weight Evaluation") {
        normalizedCategory = "Net Weight Evaluation"; // Use consistent name
      } else if (category && category.toLowerCase().startsWith("product")) {
        // Handle variations like "Product Evaluation", "Product Quality", etc.
        normalizedCategory = "Product";
      }
      
      // Initialize summary for this category if it doesn't exist
      if (!summary[normalizedCategory]) {
        summary[normalizedCategory] = { okays: 0, aDefects: 0, bDefects: 0, cDefects: 0 };
      }
      
      // Track cycles per category
      if (!cyclesPerCategory[normalizedCategory]) {
        cyclesPerCategory[normalizedCategory] = new Set<string>();
      }
      if (cycle) {
        cyclesPerCategory[normalizedCategory].add(cycle);
      }
      
      // Ensure all counts start at 0 (reset if somehow already exists)
      const categorySummary = summary[normalizedCategory];
      if (!categorySummary.okays) categorySummary.okays = 0;
      if (!categorySummary.aDefects) categorySummary.aDefects = 0;
      if (!categorySummary.bDefects) categorySummary.bDefects = 0;
      if (!categorySummary.cDefects) categorySummary.cDefects = 0;
      
      // Use normalized category for all operations
      const summaryCategory = normalizedCategory;
      
      // Debug logging for Product and Net Weight categories
      if (category === 'Product' || category === 'Net Weight' || category === 'Net Weight Evaluation') {
        console.log('Processing item:', {
          category,
          cycle,
          criteria: item.cr3ea_criteria,
          defectcategory: item.cr3ea_defectcategory,
          netweight: item.cr3ea_netweight,
          evaluationtype: item.cr3ea_evaluationtype
        });
      }
      
      // Special handling for Net Weight / Net Weight Evaluation category
      if (summaryCategory === "Net Weight Evaluation") {
        let itemCounted = false;
        
        // SIMPLIFIED: Just check if defectcategory exists (any value means it's a defect)
        const defectCatRaw = item.cr3ea_defectcategory;
        const hasDefect = defectCatRaw !== null && 
                         defectCatRaw !== undefined && 
                         defectCatRaw !== '' && 
                         String(defectCatRaw).trim() !== '' &&
                         String(defectCatRaw).toUpperCase() !== 'MISSED';
        
        // Also check if criteria is 'Not Okay'
        const isNotOkay = item.cr3ea_criteria === 'Not Okay';
        
        if (hasDefect || isNotOkay) {
          totalDefects++;
          itemCounted = true;
          
          const defectCat = normalizeDefectCategory(defectCatRaw);
          console.log(`\n🔍 [NET WEIGHT] Processing defect:`);
          console.log(`  Pack: ${item.cr3ea_packno || 'N/A'}`);
          console.log(`  Raw defectcategory: ${JSON.stringify(defectCatRaw)} (type: ${typeof defectCatRaw})`);
          console.log(`  Normalized: "${defectCat}"`);
          console.log(`  isNotOkay: ${isNotOkay}, hasDefect: ${hasDefect}`);
          
          let categoryType: 'A' | 'B' | 'C' | null = null;
          
          // Only try to detect category if we have a defect category (not just "Not Okay")
          if (hasDefect && defectCat) {
            categoryType = getDefectCategoryType(defectCat);
            console.log(`  -> Category detection result: ${categoryType}`);
            
            if (!categoryType) {
              console.log(`  ⚠️ WARNING: Could not detect category for "${defectCat}"`);
              // Try alternative detection - check the raw value directly
              const rawStr = String(defectCatRaw).toUpperCase().trim();
              console.log(`  -> Trying alternative detection on raw value: "${rawStr}"`);
              categoryType = getDefectCategoryType(rawStr);
              console.log(`  -> Alternative detection result: ${categoryType}`);
            }
          }
          
          // Count by category type
          if (categoryType === 'A') {
            summary[summaryCategory].aDefects++;
            console.log(`  ✅ COUNTED AS CATEGORY A (A total now: ${summary[summaryCategory].aDefects})`);
          } else if (categoryType === 'B') {
            summary[summaryCategory].bDefects++;
            console.log(`  ✅ COUNTED AS CATEGORY B (B total now: ${summary[summaryCategory].bDefects})`);
          } else if (categoryType === 'C') {
            summary[summaryCategory].cDefects++;
            console.log(`  ✅ COUNTED AS CATEGORY C (C total now: ${summary[summaryCategory].cDefects})`);
          } else if (isNotOkay && !hasDefect) {
            // If criteria is 'Not Okay' but no defect category, default to Category A
            summary[summaryCategory].aDefects++;
            console.log(`  ⚠️ Not Okay with no category, defaulting to Category A (A total now: ${summary[summaryCategory].aDefects})`);
          } else {
            // If we have a defect category but couldn't determine type, log it and default to A
            console.log(`  ❌ ERROR: Unknown category "${defectCat}" (raw: ${JSON.stringify(defectCatRaw)}), defaulting to Category A`);
            summary[summaryCategory].aDefects++;
            console.log(`  ⚠️ Defaulted to Category A (A total now: ${summary[summaryCategory].aDefects})`);
          }
        } 
        // Count as okay if:
        // - NOT already counted as a defect
        // - Has netweight
        // - Criteria is explicitly 'Okay' OR (no criteria AND no defect category)
        if (!itemCounted && item.cr3ea_netweight) {
          const isOkay = item.cr3ea_criteria === 'Okay';
          const noCriteria = !item.cr3ea_criteria || item.cr3ea_criteria === '' || item.cr3ea_criteria === null;
          const noDefect = !hasDefect;
          
          // Count as okay if: criteria is 'Okay' (always count if explicitly Okay)
          // OR (no criteria AND no defect - meaning it's implicitly okay)
          if (isOkay) {
            // If criteria is explicitly 'Okay', count it regardless of defect status
            summary[summaryCategory].okays++;
            totalOkays++;
            itemCounted = true;
            console.log(`[NET WEIGHT] ✅ Okay counted (explicit) for pack ${item.cr3ea_packno || 'N/A'} (total okays now: ${summary[summaryCategory].okays})`);
          } else if (noCriteria && noDefect) {
            // If no criteria and no defect, count as implicit okay
            summary[summaryCategory].okays++;
            totalOkays++;
            itemCounted = true;
            console.log(`[NET WEIGHT] ✅ Okay counted (implicit) for pack ${item.cr3ea_packno || 'N/A'} (total okays now: ${summary[summaryCategory].okays})`);
          } else {
            console.log(`[NET WEIGHT] ❌ Not counted as okay for pack ${item.cr3ea_packno || 'N/A'}: criteria=${item.cr3ea_criteria}, hasDefect=${hasDefect}, noCriteria=${noCriteria}`);
          }
        } else if (!item.cr3ea_netweight) {
          console.log(`[NET WEIGHT] ⚠️ Skipping pack ${item.cr3ea_packno || 'N/A'} - no netweight value`);
        }
      } else {
        // For other categories (CBB, Secondary, Primary, Product)
        let itemCounted = false;

      if (item.cr3ea_criteria === 'Okay') {
          summary[summaryCategory].okays++;
        totalOkays++;
          itemCounted = true;
          console.log(`[${summaryCategory}] Okay found for ${item.cr3ea_evaluationtype || 'N/A'} (total okays: ${summary[summaryCategory].okays})`);
        } else {
          // SIMPLIFIED: Check if it's a defect - either criteria is 'Not Okay' OR defectcategory exists
          const defectCatRaw = item.cr3ea_defectcategory;
          const hasDefect = defectCatRaw !== null && 
                           defectCatRaw !== undefined && 
                           defectCatRaw !== '' && 
                           String(defectCatRaw).trim() !== '' &&
                           String(defectCatRaw).toUpperCase() !== 'MISSED';
          const isNotOkay = item.cr3ea_criteria === 'Not Okay';
          
          if (hasDefect || isNotOkay) {
        totalDefects++;
            itemCounted = true;
            
            const defectCat = normalizeDefectCategory(defectCatRaw) || 'Category B';
            console.log(`[${summaryCategory}] Defect found: "${defectCat}" (raw: ${JSON.stringify(defectCatRaw)}, isNotOkay: ${isNotOkay}) for ${item.cr3ea_evaluationtype || 'N/A'}`);
            
            const categoryType = getDefectCategoryType(defectCat);
            console.log(`  -> Category detection: input="${defectCat}" (raw: ${JSON.stringify(defectCatRaw)}), detected=${categoryType}`);
            
            if (categoryType === 'A') {
              summary[summaryCategory].aDefects++;
              console.log(`  -> ✅ Counted as Category A (total A: ${summary[summaryCategory].aDefects})`);
            } else if (categoryType === 'B') {
              summary[summaryCategory].bDefects++;
              console.log(`  -> ✅ Counted as Category B (total B: ${summary[summaryCategory].bDefects})`);
            } else if (categoryType === 'C') {
              summary[summaryCategory].cDefects++;
              console.log(`  -> ✅ Counted as Category C (total C: ${summary[summaryCategory].cDefects})`);
            } else {
              // Default to Category B for Product/CBB/Secondary/Primary if unknown
              summary[summaryCategory].bDefects++;
              console.log(`  -> ⚠️ Unknown category "${defectCat}" (raw: ${JSON.stringify(defectCatRaw)}), defaulting to Category B (total B: ${summary[summaryCategory].bDefects})`);
            }
          }
        }
        
        // Log if item was not counted at all (shouldn't happen, but helps debug)
        if (!itemCounted && category === 'Net Weight' || category === 'Net Weight Evaluation') {
          console.log(`[${category}] Item NOT counted:`, {
            packno: item.cr3ea_packno,
            netweight: item.cr3ea_netweight,
            criteria: item.cr3ea_criteria,
            defectcategory: item.cr3ea_defectcategory
          });
        }
      }
    });
    
    console.log('=== CALCULATION SUMMARY ===');
    console.log('calculateSummaryStats: Summary counts:', summary);
    console.log('calculateSummaryStats: Total cycles:', uniqueCycles.size);
    console.log('calculateSummaryStats: Total defects counted:', totalDefects);
    console.log('calculateSummaryStats: Total okays counted:', totalOkays);
    
    // Log Net Weight specific summary
    if (summary['Net Weight Evaluation']) {
      console.log('=== NET WEIGHT SUMMARY ===');
      console.log('Net Weight Evaluation okays:', summary['Net Weight Evaluation'].okays);
      console.log('Net Weight Evaluation A defects:', summary['Net Weight Evaluation'].aDefects);
      console.log('Net Weight Evaluation B defects:', summary['Net Weight Evaluation'].bDefects);
      console.log('Net Weight Evaluation C defects:', summary['Net Weight Evaluation'].cDefects);
    }
    
    // Log detailed breakdown by category
    Object.entries(summary).forEach(([cat, counts]: [string, any]) => {
      const totalDefectsForCat = counts.aDefects + counts.bDefects + counts.cDefects;
      console.log(`Category: ${cat}`, {
        okays: counts.okays,
        aDefects: counts.aDefects,
        bDefects: counts.bDefects,
        cDefects: counts.cDefects,
        totalDefects: totalDefectsForCat
      });
      
      // Highlight Net Weight category with expected values
      if (cat === 'Net Weight' || cat === 'Net Weight Evaluation') {
        console.log(`\n⚠️⚠️⚠️ NET WEIGHT CATEGORY SUMMARY ⚠️⚠️⚠️`);
        console.log(`  ✅ Okays: ${counts.okays} (Expected: 2) ${counts.okays === 2 ? '✓' : '✗'}`);
        console.log(`  ✅ A Defects: ${counts.aDefects} (Expected: 3) ${counts.aDefects === 3 ? '✓' : '✗'}`);
        console.log(`  ✅ B Defects: ${counts.bDefects} (Expected: 2) ${counts.bDefects === 2 ? '✓' : '✗'}`);
        console.log(`  ✅ C Defects: ${counts.cDefects} (Expected: 2) ${counts.cDefects === 2 ? '✓' : '✗'}`);
        console.log(`  ✅ Total Defects: ${totalDefectsForCat} (Expected: 7) ${totalDefectsForCat === 7 ? '✓' : '✗'}`);
        console.log(`⚠️⚠️⚠️ END NET WEIGHT SUMMARY ⚠️⚠️⚠️\n`);
      }
    });
    console.log('=== END CALCULATION SUMMARY ===');
    
    // Calculate AGGREGATED totals across ALL categories (CBB, Secondary, Primary, Product, Net Weight)
    let aggregatedOkays = 0;
    let aggregatedADefects = 0;
    let aggregatedBDefects = 0;
    let aggregatedCDefects = 0;
    let recalculatedTotalDefects = 0;
    
    Object.values(summary).forEach((counts: any) => {
      aggregatedOkays += counts.okays || 0;
      aggregatedADefects += counts.aDefects || 0;
      aggregatedBDefects += counts.bDefects || 0;
      aggregatedCDefects += counts.cDefects || 0;
      recalculatedTotalDefects += (counts.aDefects || 0) + (counts.bDefects || 0) + (counts.cDefects || 0);
    });
    
    console.log('=== AGGREGATED TOTALS (ALL CATEGORIES) ===');
    console.log(`Total Okays (all categories): ${aggregatedOkays}`);
    console.log(`Total A Defects (all categories): ${aggregatedADefects}`);
    console.log(`Total B Defects (all categories): ${aggregatedBDefects}`);
    console.log(`Total C Defects (all categories): ${aggregatedCDefects}`);
    console.log(`Total Defects (all categories): ${recalculatedTotalDefects}`);
    console.log('=== END AGGREGATED TOTALS ===');
    
    // Use the recalculated total to ensure accuracy
    totalDefects = recalculatedTotalDefects;
    totalOkays = aggregatedOkays;


    // Calculate scores for each category
    // C7 (Nos of Hrs inspection/production) = total unique cycles across all categories
    const totalHours = uniqueCycles.size;
    const secondaryCycles = cyclesPerCategory["Secondary"] ? cyclesPerCategory["Secondary"].size : 0;
    const hasSecondary = secondaryCycles > 0;

    Object.entries(summary).forEach(([category, counts]) => {
      // If there is no Secondary cycle, completely skip Secondary from calculations
      if (!hasSecondary && category === "Secondary") {
        return;
      }

      // Use common hours (C7) for all sections, as per Excel sheet
      const categoryCycles = totalHours;
      
      // Calculate Max Potential Score based on category-specific formulas
      // Nos of Hrs inspection/production = count of completed cycles for this category
      let maxPotentialScore = 0;
      let bonusMultiplier = 0.10; // Default

      if (hasSecondary) {
        // Normal case: Secondary cycles exist
      if (category === "CBB Evaluation") {
        // CBB = 10 * 120 * Nos of Hrs inspection/production
        maxPotentialScore = 10 * 120 * categoryCycles;
          // PQI Score as per weightage = Score% * 10%
        bonusMultiplier = 0.10;
      } else if (category === "Secondary") {
        // Secondary = 120 * 2 * Nos of Hrs inspection/production
        maxPotentialScore = 120 * 2 * categoryCycles;
          // PQI Score as per weightage = Score% * 15%
        bonusMultiplier = 0.15;
      } else if (category === "Primary") {
        // Primary = 120 * 3 * Nos of Hrs inspection/production (when Secondary is present)
        maxPotentialScore = 120 * 3 * categoryCycles;
          // PQI Score as per weightage = Score% * 20%
        bonusMultiplier = 0.20;
      } else if (category === "Product") {
        // Product = 120 * 3 * Nos of Hrs inspection/production (when Secondary is present)
        maxPotentialScore = 120 * 3 * categoryCycles;
          // PQI Score as per weightage = Score% * 40%
        bonusMultiplier = 0.40;
      } else if (category === "Net Weight Evaluation" || category === "Net Weight") {
        // Net Weight = Nos of Hrs inspection/production * 120 * 15.625
        maxPotentialScore = categoryCycles * 120 * 15.625;
          // PQI Score as per weightage for Net Weight = Score% * 15%
          bonusMultiplier = 0.15;
        }
      } else {
        // Special case: NO Secondary cycles submitted
        // Use updated PQI weightages from Excel:
        // CBB 15%, Primary 25%, Product 45%, Net Weight 15%
        if (category === "CBB Evaluation") {
          maxPotentialScore = 10 * 120 * categoryCycles;
          bonusMultiplier = 0.15;
        } else if (category === "Primary") {
          maxPotentialScore = 120 * 2 * categoryCycles;
          bonusMultiplier = 0.25;
        } else if (category === "Product") {
          maxPotentialScore = 120 * 2 * categoryCycles;
          bonusMultiplier = 0.45;
        } else if (category === "Net Weight Evaluation" || category === "Net Weight") {
          maxPotentialScore = categoryCycles * 120 * 15.625;
          bonusMultiplier = 0.15;
        }
      }

      // Score deduction formula: (A_defects * 80) + (B_defects * 30) + (C_defects * 10)
      // CBB: (D4*80)+(D5*30)+(D6*10), Secondary: (E4*80)+(E5*30)+(E6*10)
      // Primary: (F4*80)+(F5*30)+(F6*10), Product: (G4*80)+(G5*30)+(G6*10)
      // Net Weight: (H4*80)+(H5*30)+(H6*10)
      // Where D4/E4/F4/G4/H4 = A defects, D5/E5/F5/G5/H5 = B defects, D6/E6/F6/G6/H6 = C defects
      const scoreDeduction = (counts.aDefects * 80) + (counts.bDefects * 30) + (counts.cDefects * 10);
      
      // Score obtained formula: Max Potential Score - Score deduction
      // CBB: +D7-D8, Secondary: +E7-E8, Primary: +F7-F8, Product: +G7-G8, Net Weight: +H7-H8
      // Where D7/E7/F7/G7/H7 = Max Potential Score, D8/E8/F8/G8/H8 = Score deduction
      const finalScore = Math.max(maxPotentialScore - scoreDeduction, 0);
      
      // Score% formula: Score obtained / Max Potential Score * 100
      // CBB: +D9/D7, Secondary: +E9/E7, Primary: +F9/F7, Product: +G9/G7, Net Weight: +H9/H7
      // Where D9/E9/F9/G9/H9 = Score obtained, D7/E7/F7/G7/H7 = Max Potential Score
      const scorePercentageValue = maxPotentialScore > 0 ? (finalScore / maxPotentialScore) * 100 : 0;
      const bonusScoreValue = scorePercentageValue * bonusMultiplier;

      // Store bonus scores individually
      if (category === "CBB Evaluation") bonusScores.cbb = bonusScoreValue;
      else if (category === "Secondary") bonusScores.secondary = bonusScoreValue;
      else if (category === "Primary") bonusScores.primary = bonusScoreValue;
      else if (category === "Product") bonusScores.product = bonusScoreValue;
      else if (category === "Net Weight Evaluation" || category === "Net Weight") bonusScores.netWeight = bonusScoreValue;

      categories.push({
        category: category,
        okays: counts.okays,
        aDefects: counts.aDefects,
        bDefects: counts.bDefects,
        cDefects: counts.cDefects,
        hours: categoryCycles, // Use count of completed cycles for this specific category
        maxScore: maxPotentialScore,
        scoreDeduction: scoreDeduction,
        scoreObtained: finalScore,
        scorePercent: scorePercentageValue,
        pqiScore: bonusScoreValue
      });
    });

    // Broken % (always 0 for now)
    const brokenPercentage = 0.00;

    // Final PQI Score = sum of all bonus scores - broken%
    const finalPQIScore = (
      bonusScores.cbb +
      bonusScores.secondary +
      bonusScores.primary +
      bonusScores.product +
      bonusScores.netWeight 
    ) - brokenPercentage;

    // PQI Status formula:
    // IF(J4>=1,"HOLD","PASS") where J4 = total A defects across all sections
    // We use aggregatedADefects (sum of all Category A defects) computed above
    const pqiStatus = aggregatedADefects >= 1 ? 'HOLD' : 'PASS';

    return { 
      totalDefects, 
      totalOkays, 
      categories,
      finalPQIScore: finalPQIScore.toFixed(2),
      pqiStatus,
      brokenPercentage: brokenPercentage.toFixed(2),
      totalHours,
      hasSecondary
    };
  };

  const summaryStats = calculateSummaryStats();

  // Check if data was restored from localStorage on component mount (only run once)
  useEffect(() => {
    const checkPersistedData = async () => {
      // Wait for persistor to rehydrate
      await persistor.persist();
      
      const persistedData = localStorage.getItem('persist:planTour');
      if (persistedData) {
        try {
          const parsed = JSON.parse(persistedData);
          console.log("Persisted planTour data found:", {
            hasSummaryData: !!parsed.summaryData,
            hasCycleData: !!parsed.cycleData,
            summaryDataLength: parsed.summaryData ? JSON.parse(parsed.summaryData).length : 0,
            cycleDataLength: parsed.cycleData ? JSON.parse(parsed.cycleData).length : 0
          });
          
          // If we have persisted data but Redux state is empty, try to restore it
          if ((!reduxSummaryData || reduxSummaryData.length === 0) && parsed.summaryData) {
            console.log("Attempting to restore summary data from localStorage");
            const summaryData = JSON.parse(parsed.summaryData);
            if (summaryData && summaryData.length > 0) {
              dispatch(setSummaryData(summaryData));
            }
          }
          
          if ((!reduxCycleData || reduxCycleData.length === 0) && parsed.cycleData) {
            console.log("Attempting to restore cycle data from localStorage");
            const cycleData = JSON.parse(parsed.cycleData);
            if (cycleData && cycleData.length > 0) {
              dispatch(setCycleData(cycleData));
            }
          }
        } catch (error) {
          console.error("Error parsing persisted data:", error);
        }
      } else {
        console.log("No persisted planTour data found in localStorage");
      }
    };
    
    checkPersistedData();
  }, []); // Only run once on mount


  return (
    <DashboardLayout>
      {/* Back Button and Plant Tour ID */}
      <div className="mb-4 flex items-center justify-between w-full">
        <div
          className="flex items-center gap-2 text-blue-600 font-medium cursor-pointer"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/home');
            }
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </div>
        {plantTourId && (
          <div className="text-sm text-gray-700 font-semibold truncate max-w-xs ml-4" title={plantTourId}>
            Plant Tour ID: <span className="text-blue-700">{plantTourId}</span>
          </div>
        )}
      </div>

      {/* Header and Summary Section (updated style) */}
      <div className="bg-gray-100 p-4 sm:p-6 rounded-lg mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
          <span className="font-semibold text-lg sm:text-xl">Product Quality Index</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-xs font-medium ml-0 sm:ml-2">
            <span className="w-2 h-2 bg-gray-500 rounded-full mr-1"></span>
            {selectedShift ? selectedShift : "Shift"}
          </span>
          <span className="text-gray-500 text-sm ml-0 sm:ml-2">• {moment().format('DD/MM/YYYY')}</span>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-6 overflow-x-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
            <div className="flex items-center gap-4">
              <span className="font-semibold text-base sm:text-lg">Summary</span>
              {isOfflineStarted && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-200 text-xs font-medium">
                  📱 Offline Data
                </span>
              )}
              {isLoadingSummary ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 text-sm font-medium">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 text-sm font-medium">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 9l-6 6m0-6l6 6" />
                  </svg>
                  {summaryStats.totalDefects} Defects
                </span>
              )}
            </div>
            <button
              className="text-blue-700 text-sm font-medium underline"
              onClick={() => setShowDetails((v) => !v)}
            >
              {showDetails ? "View less" : "View more"}
            </button>
          </div>
          {showDetails && (
            <div className="mt-4 overflow-x-auto">
              {isLoadingSummary ? (
                <div className="flex justify-center items-center py-8">
                  <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-2 text-gray-600">Loading summary data...</span>
                </div>
              ) : summaryStats.categories.length > 0 ? (
                <div className="space-y-4">
                  {/* Desktop Table View - PQI Calculator Style */}
                  <div className="hidden lg:block overflow-x-auto">
                    {(() => {
                      const baseCategoryOrder = [
                        { key: "CBB Evaluation", label: "CBB" },
                        { key: "Secondary", label: "Secondary" },
                        { key: "Primary", label: "Primary" },
                        { key: "Product", label: "Product" },
                        { key: "Net Weight Evaluation", label: "Net wt." },
                        // Extra blank column after Net Weight to match sheet
                        { key: "extra", label: "" },
                      ];

                      // When there is no Secondary cycle completed, hide the Secondary column
                      const categoryOrder = summaryStats.hasSecondary
                        ? baseCategoryOrder
                        : baseCategoryOrder.filter((c) => c.key !== "Secondary");

                      const getCat = (name: string) =>
                        summaryStats.categories.find((c: any) => c.category === name) || {};

                      return (
                        <table className="min-w-[700px] w-full text-sm text-center border border-gray-300">
                      <thead>
                            <tr className="bg-amber-100 border-b border-gray-300">
                              <th
                                className="py-2 px-2 border-r border-gray-300 font-semibold text-center"
                                colSpan={categoryOrder.length + 1}
                              >
                                PQI Calculator
                              </th>
                            </tr>
                            <tr className="bg-amber-100 border-b border-gray-300">
                              <th className="py-2 px-2 border-r border-gray-300 text-left font-semibold"></th>
                              {categoryOrder.map((cat) => (
                                <th
                                  key={cat.key}
                                  className="py-2 px-2 border-r border-gray-300 font-semibold"
                                >
                                  {cat.label}
                                </th>
                              ))}
                        </tr>
                      </thead>
                          <tbody>
                            {/* A/B/C Defects rows */}
                            <tr className="bg-red-500">
                              <td className="py-2 px-2 border-r border-gray-300 text-left font-semibold">
                                A
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-gray-300 font-semibold"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-gray-300 font-semibold"
                                  >
                                    {data.aDefects ?? 0}
                                  </td>
                                );
                              })}
                          </tr>
                            <tr className="bg-green-200">
                              <td className="py-2 px-2 border-r border-gray-300 text-left font-semibold">
                                B
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-gray-300 font-semibold"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-gray-300 font-semibold"
                                  >
                                    {data.bDefects ?? 0}
                                  </td>
                                );
                              })}
                            </tr>
                            <tr className="bg-green-400">
                              <td className="py-2 px-2 border-r border-gray-300 text-left font-semibold">
                                C
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-gray-300 font-semibold"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-gray-300 font-semibold"
                                  >
                                    {data.cDefects ?? 0}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Nos of Hrs inspection/production row (C7) */}
                            <tr className="bg-white">
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold">
                                Nos of Hrs inspection/production
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-t border-gray-300"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-t border-gray-300"
                                  >
                                    {data.hours ?? 0}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Max Potential Score */}
                            <tr className="bg-white">
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold">
                                Max Potential Score
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-t border-gray-300"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-t border-gray-300"
                                  >
                                    {data.maxScore ?? 0}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Score deduction */}
                            <tr className="bg-white">
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold">
                                Score deduction
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-t border-gray-300"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-t border-gray-300"
                                  >
                                    {data.scoreDeduction ?? 0}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Score obtained */}
                            <tr className="bg-white">
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold">
                                Score obtained
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-t border-gray-300"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-t border-gray-300"
                                  >
                                    {data.scoreObtained ?? 0}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Score % */}
                            <tr className="bg-white">
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold">
                                Score%
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-t border-gray-300"
                                    ></td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                const percent =
                                  data.scorePercent?.toFixed
                                    ? data.scorePercent.toFixed(2)
                                    : data.scorePercent ?? 0;
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-t border-gray-300"
                                  >
                                    {percent}%
                                  </td>
                                );
                              })}
                            </tr>

                            {/* PQI Score as per weightage */}
                            <tr className="bg-white">
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold">
                                PQI Score as per weightage
                              </td>
                              {categoryOrder.map((cat) => {
                                if (cat.key === "extra") {
                                  // Overall / total PQI score column (last column in sheet)
                                  const overall =
                                    Number(summaryStats.finalPQIScore) || 0;
                                  return (
                                    <td
                                      key={cat.key}
                                      className="py-2 px-2 border-r border-t border-gray-300 font-semibold"
                                    >
                                      {overall.toFixed(2)}%
                                    </td>
                                  );
                                }
                                const data: any = getCat(cat.key);
                                const pqi =
                                  data.pqiScore?.toFixed
                                    ? data.pqiScore.toFixed(2)
                                    : data.pqiScore ?? 0;
                                return (
                                  <td
                                    key={cat.key}
                                    className="py-2 px-2 border-r border-t border-gray-300"
                                  >
                                    {pqi}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Nos of Hrs inspection/production column removed as per requirement */}

                        {/* Broken % row */}
                            <tr>
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold bg-yellow-400">
                                Broken %
                              </td>
                              <td
                                colSpan={categoryOrder.length}
                                className="py-2 px-2 border-t border-gray-300"
                              >
                            <input
                              type="text"
                              value={brokenPercentage}
                              onChange={(e) => setBrokenPercentage(e.target.value)}
                              placeholder="Enter Broken %"
                                  className="w-full max-w-xs mx-auto px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                        </tr>

                        {/* Final PQI Score row */}
                            <tr>
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold bg-orange-300">
                                Final PQI Score post deduction of broken
                              </td>
                              <td
                                colSpan={categoryOrder.length}
                                className="py-2 px-2 border-t border-gray-300 font-semibold"
                              >
                                {Math.max(
                                  0,
                                  (Number(summaryStats.finalPQIScore) || 0) -
                                    parseFloat(brokenPercentage || "0")
                                ).toFixed(2)}
                                %
                          </td>
                        </tr>

                        {/* PQI Status row */}
                            <tr>
                              <td className="py-2 px-2 border-r border-t border-gray-300 text-left font-semibold bg-sky-300">
                                PQI Status
                              </td>
                              <td
                                colSpan={categoryOrder.length}
                                className="py-2 px-2 border-t border-gray-300"
                              >
                                <span
                                  className={`inline-block min-w-[120px] text-center px-4 py-1 rounded-full text-white font-bold text-sm ${
                                    Math.max(
                                      0,
                                      (Number(summaryStats.finalPQIScore) || 0) -
                                        parseFloat(brokenPercentage || "0")
                                    ) >= 90
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                >
                                  {Math.max(
                                    0,
                                    (Number(summaryStats.finalPQIScore) || 0) -
                                      parseFloat(brokenPercentage || "0")
                                  ) >= 90
                                    ? "PASS"
                                    : "HOLD"}
                                </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                      );
                    })()}
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-4">
                    {summaryStats.categories.map((cat: any, idx: number) => (
                      <div key={idx} className="bg-white border rounded-lg p-4 border-gray-200">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-semibold text-gray-900">{cat.category}</h3>
                          <span className="text-sm font-medium text-blue-600">
                            {cat.pqiScore?.toFixed ? cat.pqiScore.toFixed(2) : (cat.pqiScore ?? 0).toFixed?.(2) || '0.00'}%
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Okays:</span>
                            <span className="font-medium text-green-600">{cat.okays}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">A Defects:</span>
                            <span className="font-medium text-red-600">{cat.aDefects}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">B Defects:</span>
                            <span className="font-medium text-red-600">{cat.bDefects}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">C Defects:</span>
                            <span className="font-medium text-red-600">{cat.cDefects}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Hours:</span>
                            <span className="font-medium">{cat.hours}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Score %:</span>
                            <span className="font-medium">
                              {cat.scorePercent?.toFixed ? cat.scorePercent.toFixed(2) : cat.scorePercent}
                              %
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Mobile Summary Cards */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex flex-col gap-2">
                        <span className="font-semibold text-green-800">Broken %</span>
                        <input
                          type="text"
                          value={brokenPercentage}
                          onChange={(e) => setBrokenPercentage(e.target.value)}
                          placeholder="Enter Broken %"
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-yellow-800">Final PQI Score post deduction of broken</span>
                        <span className="text-yellow-700">{Math.max(0, (Number(summaryStats.finalPQIScore) || 0) - parseFloat(brokenPercentage || '0')).toFixed(2)}%</span>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-red-800">PQI Status</span>
                        <span className={`px-3 py-1 rounded-full text-white font-bold text-sm ${(Math.max(0, (Number(summaryStats.finalPQIScore) || 0) - parseFloat(brokenPercentage || '0')) >= 90) ? 'bg-green-500' : 'bg-red-500'}`}>
                          {(Math.max(0, (Number(summaryStats.finalPQIScore) || 0) - parseFloat(brokenPercentage || '0')) >= 90) ? 'PASS' : 'HOLD'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No summary data available for this tour.
                </div>
              )}
            </div>
          )}
        </div>
        

      </div>

      {/* Main Content - Cycles */}
      <div className="flex flex-col md:grid md:grid-cols-4 gap-4">
        {/* Sidebar (page-specific) */}
        <div className="space-y-2 md:col-span-1">
          <button 
            className={`w-full text-left px-4 py-2 rounded-md font-medium border ${
              selectedEvaluationType === 'CBB' 
                ? 'bg-blue-100 text-blue-600 border-blue-200' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedEvaluationType('CBB')}
          >
            CBB Evaluation
          </button>
          <button 
            className={`w-full text-left px-4 py-2 rounded-md font-medium border ${
              selectedEvaluationType === 'Secondary' 
                ? 'bg-blue-100 text-blue-600 border-blue-200' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedEvaluationType('Secondary')}
          >
            Secondary
          </button>

          <button 
            className={`w-full text-left px-4 py-2 rounded-md font-medium border ${
              selectedEvaluationType === 'Primary' 
                ? 'bg-blue-100 text-blue-600 border-blue-200' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedEvaluationType('Primary')}
          >
            Primary
          </button>
          <button 
            className={`w-full text-left px-4 py-2 rounded-md font-medium border ${
              selectedEvaluationType === 'Product' 
                ? 'bg-blue-100 text-blue-600 border-blue-200' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedEvaluationType('Product')}
          >
            Product
          </button>
          <button 
            className={`w-full text-left px-4 py-2 rounded-md font-medium border ${
              selectedEvaluationType === 'NetWeight' 
                ? 'bg-blue-100 text-blue-600 border-blue-200' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedEvaluationType('NetWeight')}
          >
            Net Weight
          </button>
        </div>
        {/* Main Content - Cycles */}
        <div className="md:col-span-3">
          {selectedEvaluationType === 'CBB' && (
            <CBBEvaluation
              onCycleComplete={onCycleComplete}
            />
          )}
          {selectedEvaluationType === 'Secondary' && (
            <SecondaryEvaluation
              onCycleComplete={onCycleComplete}
            />
          )}
          {selectedEvaluationType === 'Primary' && (
            <PrimaryEvaluation
              onCycleComplete={onCycleComplete}
            />
          )}
          {selectedEvaluationType === 'Product' && (
            <ProductEvaluation
              onCycleComplete={onCycleComplete}
            />
          )}
          {selectedEvaluationType === 'NetWeight' && (
            <NetWeightEvaluation
              onCycleComplete={onCycleComplete}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProductQualityIndex;
